import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BaseComponent } from '@ids-components';
import { PROJECT_MANAGEMENT_CONSTANTS, THREAT_STATUS_OPTIONS, THREAT_TYPES, THREAT_TYPE_DESCRIPTIONS } from '@ids-constants';
import { AnomalyAnalyzerService, ConnectionService } from '@ids-services';
import { ORGANIZATION_ID_PARAM_ROUTE, PROJECT_ID_PARAM_ROUTE } from '@microsec/constants';
import { CommonChart } from '@microsec/models';
import { MODULES } from 'apps/ids-frontend/src/app/products/modules';
import { ANOMALY_ANALYZER_CONSTANTS } from 'apps/ids-frontend/src/app/products/project-management/configuration/anomaly-analyzer/anomaly-analyzer.config';
import { NETWORK_SETTINGS_CONSTANTS } from 'apps/ids-frontend/src/app/products/project-management/configuration/network-settings/network-settings.config';
import { CHARTS, CHART_KEYS, getGradientBackgroundColor } from './threat-group-overview.config';
import { Chart, ChartData, ChartDataset } from 'chart.js';
import moment from 'moment';
import { CommonModule, UpperCasePipe } from '@angular/common';
import { CardComponent } from '@microsec/components';
import { ConstantPipe, MomentPipe, ThreatScorePipe } from '@ids-pipes';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { ChipModule } from 'primeng/chip';
import { TimelineModule } from 'primeng/timeline';
import { ChartModule } from 'primeng/chart';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { DrawerModule } from 'primeng/drawer';
import { ThreatDetailsComponent } from '../../../../../../../shared-components/shared-threats/threat-details/threat-details.component';

const FORMATS = {
  ['present']: {
    ['hour']: 'HH[:00]',
    ['day']: 'ddd',
    ['week']: '[Week] W',
    ['month']: 'MMM',
    ['year']: 'YYYY',
  },
  ['past']: {
    ['hour']: 'YYYY-MM-DD HH[:00]',
    ['day']: 'YYYY-MM-DD',
    ['week']: '[Week] W (YYYY)',
    ['month']: 'MMM (YYYY)',
    ['year']: 'YYYY',
  },
};

@Component({
  selector: 'app-threat-group-overview',
  templateUrl: './threat-group-overview.component.html',
  styleUrls: ['./threat-group-overview.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TimelineModule,
    TooltipModule,
    SelectModule,
    SelectButtonModule,
    DrawerModule,
    ChartModule,
    ChipModule,
    CardComponent,
    ThreatScorePipe,
    ConstantPipe,
    UpperCasePipe,
    MomentPipe,
    ThreatDetailsComponent,
  ],
  providers: [ThreatScorePipe, ConstantPipe, UpperCasePipe],
})
export class ThreatGroupOverviewComponent extends BaseComponent implements OnInit {
  _threatGroup: any = null;

  get threatGroup() {
    return this._threatGroup;
  }

  @Input() set threatGroup(value: any) {
    this._threatGroup = value;
    this.threatGroup.descriptionPreview = this.util.parseMarkdown(value?.description || '');
    this.threats = this.util.sortObjectArray(this.util.cloneObjectArray(this.threatGroup?.threats || []), 'created', true, true);
    this.devices = this.util.sortObjectArray(this.util.cloneObjectArray(this.threatGroup?.devices || []), 'threat_count', false).slice(0, 5);
    this.setFrequencyChartData();
    this.setDevicesChartData();
  }

  threats: any[] = [];

  devices: any[] = [];

  detailsViewOptions = [
    { value: 'timeline', label: 'Threat Timeline' },
    { value: 'frequency', label: 'Threat Frequency' },
    { value: 'devices', label: 'Top Impacted Devices' },
  ];

  selectedDetailsView = 'timeline';

  charts: { [key: string]: CommonChart } = {
    [CHART_KEYS.FREQUENCY]: this.util.cloneDeepObject(CHARTS[CHART_KEYS.FREQUENCY]),
    [CHART_KEYS.DEVICES]: this.util.cloneDeepObject(CHARTS[CHART_KEYS.DEVICES]),
  };

  displayThreatDetails = false;

  _selectedThreat = null;

  get selectedThreat() {
    return this._selectedThreat;
  }

  set selectedThreat(value: any) {
    this._selectedThreat = value;
    this.displayThreatDetails = !!value;
  }

  THREAT_TYPES = THREAT_TYPES;

  THREAT_TYPE_DESCRIPTIONS = THREAT_TYPE_DESCRIPTIONS;

  threatStatusOptions = this.util.cloneObjectArray(THREAT_STATUS_OPTIONS);

  @ViewChild('frequencyDiagram') frequencyDiagram!: ElementRef;

  @Output() changeStatusEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() openDeviceTabEvent: EventEmitter<any> = new EventEmitter<any>();

  frequencyChart: Chart | null = null;

  constructor(
    private connectionSrv: ConnectionService,
    private anomalyAnalyzerSrv: AnomalyAnalyzerService,
  ) {
    super();
  }

  async ngOnInit() {
    await this.prepareConfigs();
  }

  getFrequencyData() {
    const data: { [key: string]: number } = {};
    if (this.threats.length) {
      const currDate = moment();
      const firstDate = moment.utc(this.threats[0].created).local().set({ minute: 0, second: 0, millisecond: 0 });
      const lastDate = moment
        .utc(this.threats[this.threats.length - 1].created)
        .local()
        .set({ minute: 0, second: 0, millisecond: 0 });
      const duration = moment.duration(lastDate.diff(firstDate));
      const seconds = duration.asSeconds();

      const range = seconds < 86400 ? 'hour' : seconds < 604800 ? 'day' : seconds < 2419200 ? 'week' : seconds < 31536000 ? 'month' : 'year';
      const format =
        FORMATS[
          ((range === 'month' || range === 'week') && lastDate.year() !== currDate.year()) ||
          (range === 'day' && (lastDate.isoWeek() !== currDate.isoWeek() || lastDate.year() !== currDate.year())) ||
          (range === 'hour' && (lastDate.day() !== currDate.day() || lastDate.month() !== currDate.month() || lastDate.year() !== currDate.year()))
            ? 'past'
            : 'present'
        ][range];
      const startDate = moment(firstDate);
      const endDate = moment(lastDate).add(1, range);
      switch (range) {
        case 'day': {
          startDate.hour(0);
          startDate.hour(0);
          break;
        }
        case 'week': {
          startDate.isoWeekday(1).hour(0);
          startDate.isoWeekday(1).hour(0);
          break;
        }
        case 'month': {
          startDate.date(1).hour(0);
          endDate.date(1).hour(0);
          break;
        }
        case 'year': {
          startDate.month(1).date(1).hour(0);
          endDate.month(1).date(1).hour(0);
          break;
        }
        default: {
          break;
        }
      }
      while (startDate.isBefore(endDate)) {
        data[startDate.format(format)] = 0;
        startDate.add(1, range);
      }
      this.threats.forEach((e) => {
        const date = moment.utc(e.created).local();
        data[date.format(format)] += 1;
      });
    }
    return data;
  }

  setFrequencyChartData() {
    const chart: CommonChart = this.util.cloneDeepObject(this.charts[CHART_KEYS.FREQUENCY]);
    const color = '#fb4d58';
    const chartsData = chart.data as ChartData;
    const data = this.getFrequencyData();
    chartsData.labels = Object.keys(data);
    chartsData.datasets = [
      {
        data: Object.values(data),
        fill: true,
        lineTension: 0.2,
        borderWidth: 3,
        borderColor: color,
        backgroundColor: (context: any) => {
          const { ctx } = context.chart;
          return getGradientBackgroundColor(ctx, color);
        },
        pointBackgroundColor: color,
      } as ChartDataset,
    ];
    this.charts[CHART_KEYS.FREQUENCY] = chart;
  }

  setDevicesChartData() {
    const chart: CommonChart = this.util.cloneDeepObject(this.charts[CHART_KEYS.DEVICES]);
    const color = '#fb4d58';
    const chartsData = chart.data as ChartData;
    chartsData.labels = this.devices.map((d) => d.label);
    chartsData.datasets = [
      {
        data: this.devices.map((d) => d.threat_count),
        borderWidth: 1,
        borderColor: this.devices.map(() => color),
        backgroundColor: this.devices.map(() => this.util.hexToRgba(color)),
      } as ChartDataset,
    ];
    this.charts[CHART_KEYS.DEVICES] = chart;
  }

  goToConnectionPage(connection_id: any) {
    this.connectionSrv.selected = !!connection_id ? { id: connection_id } : null;
    this.changeRoute(
      `${MODULES.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.NETWORK_SETTINGS.ROUTE}/${NETWORK_SETTINGS_CONSTANTS.NETWORKS.ROUTE}`,
    );
  }

  goToAnalyzerPage(analyzer_id: any, isRuleBased: boolean, isSnort = false) {
    this.anomalyAnalyzerSrv.selected = !!analyzer_id ? { id: analyzer_id, isSnort: isSnort } : {};
    const analyzerRoute = `${!!isRuleBased ? ANOMALY_ANALYZER_CONSTANTS.RULE_BASED.ROUTE : ANOMALY_ANALYZER_CONSTANTS.MACHINE_LEARNING.ROUTE}`;
    this.changeRoute(
      `${MODULES.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.ANOMALY_ANALYZER.ROUTE}/${analyzerRoute}`,
    );
  }

  openUrl(url: any) {
    window.open(url, '_blank');
  }

  openSnortUrl(snortRuleId: string) {
    const param: any[] = snortRuleId.split(':') || [];
    if (parseInt(param[0]) === 1 && parseInt(param[1]) >= 1000000) {
      this.goToAnalyzerPage(this.threatGroup.analyzer_id, true, true);
    } else {
      this.openUrl('https://www.snort.org/rule_docs/' + param.slice(0, 2).join('-'));
    }
  }
}
